<template>
  <nav class="navbar">
    <div class="container">
      <div class="nav-content">
        <router-link to="/" class="nav-logo">
          <img src="/img/logo_text.png" alt="Invoice Scan" class="logo-text" style="height: 36px" />
        </router-link>

        <div class="nav-links" :class="{ 'mobile-open': mobileMenuOpen }">
          <router-link to="/about" class="nav-link" @click="closeMobileMenu">
            <svg class="nav-icon" style="width: 1em; height: 1em;" viewBox="0 0 384 512" fill="currentColor">
              <path d="M64 0C28.7 0 0 28.7 0 64L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-384c0-35.3-28.7-64-64-64L64 0zM176 352l32 0c17.7 0 32 14.3 32 32l0 80-96 0 0-80c0-17.7 14.3-32 32-32zM96 112c0-8.8 7.2-16 16-16l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32zM240 96l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32c0-8.8 7.2-16 16-16zM96 240c0-8.8 7.2-16 16-16l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32zm144-16l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32c0-8.8 7.2-16 16-16z"/>
            </svg>
            {{ $t('welcome.nav.about') || 'About' }}
          </router-link>
          <router-link to="/pricing" class="nav-link" @click="closeMobileMenu">
            <font-awesome-icon icon="fa-solid fa-credit-card" class="nav-icon" />
            {{ $t('welcome.nav.pricing') || 'Pricing' }}
          </router-link>
          <router-link to="/integrations" class="nav-link" @click="closeMobileMenu">
            <font-awesome-icon icon="fa-solid fa-puzzle-piece" class="nav-icon" />
            {{ $t('welcome.nav.integrations') || 'Integrations' }}
          </router-link>
          <router-link to="/contact" class="nav-link" @click="closeMobileMenu">
            <font-awesome-icon icon="fa-solid fa-paper-plane" class="nav-icon" />
            {{ $t('welcome.nav.contact') || 'Contact Us' }}
          </router-link>
        </div>
        <div class="nav-actions">
          <div class="language-selector">
            <button @click="toggleLanguageDropdown" class="language-btn" aria-label="Change language">
              {{ currentLocale.toUpperCase() }}
            </button>
            <div v-if="showLanguageDropdown" class="language-dropdown">
              <button @click="setLocale('en')" :class="{ active: currentLocale === 'en' }">English</button>
              <button @click="setLocale('bg')" :class="{ active: currentLocale === 'bg' }">Български</button>
              <button @click="setLocale('ar')" :class="{ active: currentLocale === 'ar' }">العربية</button>
              <button @click="setLocale('he')" :class="{ active: currentLocale === 'he' }">עברית</button>
              <button @click="setLocale('el')" :class="{ active: currentLocale === 'el' }">Ελληνικά</button>
            </div>
          </div>
          <button @click="toggleMobileMenu" class="mobile-menu-btn" aria-label="Toggle menu">
            <font-awesome-icon :icon="mobileMenuOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'" />
          </button>
          <a :href="loginUrl" class="btn-nav-login desktop-cta">
            {{ $t('welcome.nav.getStarted') || 'Get Started' }}
          </a>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { locale } = useI18n();
const currentLocale = ref(locale.value);
const showLanguageDropdown = ref(false);
const mobileMenuOpen = ref(false);

const googleLoginUrl = computed(() => {
  return '/auth/google';
});


const loginUrl = computed(() => {
  return '/login';
});

const toggleLanguageDropdown = () => {
  showLanguageDropdown.value = !showLanguageDropdown.value;
};

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
};

const setLocale = (newLocale) => {
  currentLocale.value = newLocale;
  locale.value = newLocale;
  localStorage.setItem('locale', newLocale);
  showLanguageDropdown.value = false;
};

// Close dropdown when clicking outside
if (typeof window !== 'undefined') {
  window.addEventListener('click', (e) => {
    if (!e.target.closest('.language-selector')) {
      showLanguageDropdown.value = false;
    }
  });
}
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.navbar {
  border-bottom: 1px solid #e2e8f0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.nav-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 32px;
}

.mobile-menu-btn {
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: #64748b;
  cursor: pointer;
  padding: 8px;
  transition: color 0.2s ease;
}

.mobile-menu-btn:hover {
  color: #1e293b;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  transition: color 0.2s ease;
}

.nav-link:hover {
  color: #1e293b;
}

.nav-icon {
  font-size: 16px;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.language-selector {
  position: relative;
}

.language-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  height: 40px;
  padding: 0 12px;
  background: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 600;
}

.language-btn:hover {
  background: #f8fafc;
  border-color: #0ea5e9;
  color: #0ea5e9;
}

.language-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  overflow: hidden;
  z-index: 1000;
}

.language-dropdown button {
  width: 100%;
  padding: 12px 16px;
  background: white;
  border: none;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.language-dropdown button:hover {
  background: #f8fafc;
  color: #1e293b;
}

.language-dropdown button.active {
  background: #eff6ff;
  color: #0ea5e9;
  font-weight: 600;
}

.btn-contact-sales {
  display: inline-block;
  padding: 8px 20px;
  background: transparent;
  color: #0ea5e9;
  border: 2px solid #0ea5e9;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  margin-left: 8px;
}

.btn-contact-sales:hover {
  background: #0ea5e9;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.btn-nav-login {
  display: inline-block;
  padding: 10px 24px;
  background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
  color: white;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.3);
}

.btn-nav-login:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

@media (max-width: 1024px) {
  .mobile-menu-btn {
    display: block;
  }

  .desktop-cta {
    display: none;
  }

  .nav-links {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    padding: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-top: 1px solid #e2e8f0;
  }

  .nav-links.mobile-open {
    display: flex;
  }

  .nav-link {
    width: 100%;
    padding: 16px 24px;
    border-bottom: 1px solid #f1f5f9;
  }

  .nav-link:hover {
    background: #f8fafc;
  }
}

@media (max-width: 768px) {
  .nav-content {
    padding: 12px 0;
  }
}
</style>
